import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, Building2, Receipt, Paperclip, Upload, X, Pencil, Wallet, Download } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { AccountingLayout } from '../components/AccountingLayout';
import { AccountCombobox } from '../components/AccountCombobox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listVendors, createVendor, updateVendor, deleteVendor,
  listVendorBills, createVendorBill, updateVendorBill, deleteVendorBill,
  createBillPayment,
  listAccounts, uploadFile, attachmentUrl,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);

/* ---------------- aging & tempo helpers (dinamis, tidak disimpan) ---------------- */
const TEMPO_PRESETS = [0, 7, 14, 30, 45, 60, 90];
const fmtDateID = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
const addDays = (iso, d) => {
  if (!iso) return '';
  const t = new Date(`${iso}T00:00:00`);
  if (isNaN(t)) return '';
  t.setDate(t.getDate() + Number(d || 0));
  return t.toISOString().slice(0, 10);
};
const dayDiff = (fromIso, toIso) =>
  Math.round((new Date(`${toIso}T00:00:00`) - new Date(`${fromIso}T00:00:00`)) / 86400000);

const TEMPO_STATUS = {
  belum_tempo: { label: 'Belum Jatuh Tempo', cls: 'bg-stone-100 text-stone-600 border-stone-200' },
  hari_ini:    { label: 'Jatuh Tempo Hari Ini', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  terlambat:   { label: 'Terlambat', cls: 'bg-red-50 text-red-700 border-red-200' },
  lunas:       { label: 'Lunas', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};
const AGE_BUCKETS = [
  { k: '0-7',   label: '0–7 hari',   test: (a) => a <= 7 },
  { k: '8-30',  label: '8–30 hari',  test: (a) => a >= 8 && a <= 30 },
  { k: '31-60', label: '31–60 hari', test: (a) => a >= 31 && a <= 60 },
  { k: '61-90', label: '61–90 hari', test: (a) => a >= 61 && a <= 90 },
  { k: '90+',   label: '>90 hari',   test: (a) => a > 90 },
];
const SORT_OPTS = [
  { k: 'date_desc',   label: 'Tanggal terbaru' },
  { k: 'date_asc',    label: 'Tanggal terlama' },
  { k: 'amount_desc', label: 'Nominal terbesar' },
  { k: 'amount_asc',  label: 'Nominal terkecil' },
  { k: 'age_desc',    label: 'Umur hutang terbesar' },
  { k: 'age_asc',     label: 'Umur hutang terkecil' },
  { k: 'due_asc',     label: 'Jatuh tempo terdekat' },
  { k: 'due_desc',    label: 'Jatuh tempo terlama' },
];

const enrichBill = (b, today) => {
  const paid = Number(b.paid_amount || 0);
  const sisa = Math.max(0, Number(b.amount || 0) - paid);
  const lunas = sisa <= 0.004;
  const termDays = Math.max(0, Number(b.term_days || 0));
  const due = b.due_date || (b.date ? addDays(b.date, termDays) : '');
  const age = b.date ? Math.max(0, dayDiff(b.date, today)) : 0;
  const daysToDue = due ? dayDiff(today, due) : 0;
  const payStatus = lunas ? 'lunas' : paid > 0 ? 'sebagian' : 'belum';
  const tempoStatus = lunas ? 'lunas' : daysToDue > 0 ? 'belum_tempo' : daysToDue === 0 ? 'hari_ini' : 'terlambat';
  return { ...b, paid, sisa, lunas, termDays, due, age, daysToDue, payStatus, tempoStatus };
};

const tempoLabel = (b) => (b.termDays === 0 ? 'Cash' : `${b.termDays} hari`);
const payStatusLabel = (b) => (b.payStatus === 'lunas' ? 'LUNAS' : b.payStatus === 'sebagian' ? 'SEBAGIAN' : 'BELUM');

/* ---------------- export excel ---------------- */
const exportBillsToExcel = (rows) => {
  if (!rows.length) { toast.error('Tidak ada tagihan untuk diexport'); return; }
  const rpFmt = '"Rp"#,##0';
  const dateFmt = 'dd/mm/yyyy';
  const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: 'FFCCCCCC' } }, bottom: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      left: { style: 'thin', color: { rgb: 'FFCCCCCC' } }, right: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
    },
  };
  const titleStyle = {
    font: { bold: true, size: 14, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const totalStyle = {
    font: { bold: true },
    fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } },
    border: { top: { style: 'medium', color: { rgb: 'FF065F46' } } },
  };
  const asDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return isNaN(d) ? (iso || '') : { v: d, t: 'd', z: dateFmt };
  };
  const cols = 13;
  const aoa = [];
  aoa.push([{ v: 'LAPORAN HUTANG USAHA (VENDOR & TAGIHAN)', s: titleStyle }, ...Array(cols - 1).fill('')]);
  aoa.push([{ v: `Diekspor: ${new Date().toLocaleString('id-ID')} · ${rows.length} tagihan` }]);
  aoa.push([]);
  aoa.push([
    'No', 'Tanggal Tagihan', 'Vendor', 'Deskripsi', 'Nominal Tagihan', 'Sudah Dibayar',
    'Sisa Hutang', 'Tempo', 'Jatuh Tempo', 'Umur Hutang', 'Status Tempo', 'Status Pembayaran', 'Ref',
  ].map((h) => ({ v: h, s: headerStyle })));
  let sumAmt = 0, sumPaid = 0, sumSisa = 0;
  rows.forEach((b, i) => {
    sumAmt += Number(b.amount || 0); sumPaid += b.paid; sumSisa += b.sisa;
    aoa.push([
      i + 1,
      asDate(b.date),
      b.vendor_name || '',
      b.description || '',
      { v: Number(b.amount || 0), t: 'n', z: rpFmt },
      { v: b.paid, t: 'n', z: rpFmt },
      { v: b.sisa, t: 'n', z: rpFmt },
      tempoLabel(b),
      asDate(b.due),
      b.lunas ? 'LUNAS' : `${b.age} hari`,
      TEMPO_STATUS[b.tempoStatus].label.toUpperCase(),
      payStatusLabel(b),
      b.journal_ref || '',
    ]);
  });
  aoa.push([
    { v: 'TOTAL', s: totalStyle }, '', '', '',
    { v: sumAmt, t: 'n', z: rpFmt, s: totalStyle },
    { v: sumPaid, t: 'n', z: rpFmt, s: totalStyle },
    { v: sumSisa, t: 'n', z: rpFmt, s: { ...totalStyle, font: { bold: true, color: { rgb: 'FF065F46' } } } },
    '', '', '', '', '', '',
  ]);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 5 }, { wch: 14 }, { wch: 28 }, { wch: 32 }, { wch: 16 }, { wch: 14 },
    { wch: 16 }, { wch: 9 }, { wch: 13 }, { wch: 11 }, { wch: 18 }, { wch: 15 }, { wch: 12 },
  ];
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols - 1 } }];
  ws['!rows'] = [{ hpt: 26 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Hutang Usaha');
  XLSX.writeFile(wb, `Laporan_Hutang_${todayIso()}.xlsx`);
  toast.success(`Excel diunduh (${rows.length} tagihan)`);
};

/* ---------------- page ---------------- */
export default function VendorsPage() {
  const [tab, setTab] = useState('bills');
  const [vendors, setVendors] = useState([]);
  const [bills, setBills] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [openVendor, setOpenVendor] = useState(false);
  const [openBill, setOpenBill] = useState(false);
  const [autoSelectAfterCreate, setAutoSelectAfterCreate] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [editingBillId, setEditingBillId] = useState(null);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({ date: todayIso(), amount: '', cash_account_id: '', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', contact: '', address: '', notes: '' });
  const [billForm, setBillForm] = useState({
    vendor_id: '', date: todayIso(), description: '', amount: '', term_days: 0,
    expense_account_id: '', payable_account_id: '',
    attachment_url: '', attachment_name: '',
  });

  // Hutang: filter, sort, seleksi
  const [fPay, setFPay] = useState('all');
  const [fTempo, setFTempo] = useState('all');
  const [fAge, setFAge] = useState('all');
  const [fVendor, setFVendor] = useState('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedBillIds, setSelectedBillIds] = useState(new Set());

  const refresh = async () => {
    const [v, b, a] = await Promise.all([
      listVendors(), listVendorBills(), listAccounts({ is_active: true }),
    ]);
    setVendors(v); setBills(b); setAccounts(a);
    setBillForm((f) => {
      const payable = f.payable_account_id ? null : a.find((x) => x.code === '2-1000');
      const expense = f.expense_account_id ? null : a.find((x) => x.code === '1-1400');
      const patch = {};
      if (payable) patch.payable_account_id = payable.id;
      if (expense) patch.expense_account_id = expense.id;
      return { ...f, ...patch };
    });
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const expenseAccounts = useMemo(() => accounts.filter((a) => a.type === 'expense' || a.type === 'asset'), [accounts]);
  const liabilityAccounts = useMemo(() => accounts.filter((a) => a.type === 'liability'), [accounts]);

  /* ---------- hutang: enrich (umur & tempo dinamis dari tanggal hari ini) ---------- */
  const enriched = useMemo(() => bills.map((b) => enrichBill(b, todayIso())), [bills]);

  const filtered = useMemo(() => {
    let arr = enriched;
    if (fPay !== 'all') arr = arr.filter((b) => b.payStatus === fPay);
    if (fTempo !== 'all') arr = arr.filter((b) => b.tempoStatus === fTempo);
    if (fAge !== 'all') {
      const bucket = AGE_BUCKETS.find((x) => x.k === fAge);
      if (bucket) arr = arr.filter((b) => bucket.test(b.age));
    }
    if (fVendor !== 'all') arr = arr.filter((b) => b.vendor_id === fVendor);
    if (fFrom) arr = arr.filter((b) => b.date >= fFrom);
    if (fTo) arr = arr.filter((b) => b.date <= fTo);
    const sorted = [...arr];
    switch (sortBy) {
      case 'date_asc': sorted.sort((a, b) => a.date.localeCompare(b.date)); break;
      case 'amount_desc': sorted.sort((a, b) => b.amount - a.amount); break;
      case 'amount_asc': sorted.sort((a, b) => a.amount - b.amount); break;
      case 'age_desc': sorted.sort((a, b) => b.age - a.age); break;
      case 'age_asc': sorted.sort((a, b) => a.age - b.age); break;
      case 'due_asc': sorted.sort((a, b) => a.due.localeCompare(b.due)); break;
      case 'due_desc': sorted.sort((a, b) => b.due.localeCompare(a.due)); break;
      default: sorted.sort((a, b) => b.date.localeCompare(a.date));
    }
    return sorted;
  }, [enriched, fPay, fTempo, fAge, fVendor, fFrom, fTo, sortBy]);

  const filterActive = fPay !== 'all' || fTempo !== 'all' || fAge !== 'all' || fVendor !== 'all' || !!fFrom || !!fTo;
  const resetFilters = () => { setFPay('all'); setFTempo('all'); setFAge('all'); setFVendor('all'); setFFrom(''); setFTo(''); };

  // Summary keseluruhan (data aktual tersimpan, bukan hasil filter)
  const activeBills = useMemo(() => enriched.filter((b) => !b.lunas), [enriched]);
  const totalHutang = activeBills.reduce((s, b) => s + b.sisa, 0);
  const belumTempoAmt = activeBills.filter((b) => b.tempoStatus !== 'terlambat').reduce((s, b) => s + b.sisa, 0);
  const terlambatAmt = activeBills.filter((b) => b.tempoStatus === 'terlambat').reduce((s, b) => s + b.sisa, 0);

  // Aging dashboard berdasarkan SISA hutang aktif
  const aging = useMemo(() => ({
    current: activeBills.filter((b) => b.age <= 30).reduce((s, b) => s + b.sisa, 0),
    d3160: activeBills.filter((b) => b.age >= 31 && b.age <= 60).reduce((s, b) => s + b.sisa, 0),
    d6190: activeBills.filter((b) => b.age >= 61 && b.age <= 90).reduce((s, b) => s + b.sisa, 0),
    d90: activeBills.filter((b) => b.age > 90).reduce((s, b) => s + b.sisa, 0),
  }), [activeBills]);

  // Seleksi (client-side, realtime tanpa reload)
  const toggleBill = (id) => setSelectedBillIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every((b) => selectedBillIds.has(b.id));
  const toggleAllBills = () => setSelectedBillIds((s) => {
    if (allFilteredSelected) { const n = new Set(s); filtered.forEach((b) => n.delete(b.id)); return n; }
    const n = new Set(s); filtered.forEach((b) => n.add(b.id)); return n;
  });
  const selectedBills = enriched.filter((b) => selectedBillIds.has(b.id));
  const selectedTotal = selectedBills.reduce((s, b) => s + b.sisa, 0);

  const saveVendor = async () => {
    if (!vendorForm.name.trim()) return toast.error('Nama wajib');
    try {
      let created;
      if (editingVendorId) {
        created = await updateVendor(editingVendorId, vendorForm);
        toast.success('Vendor diperbarui');
      } else {
        created = await createVendor(vendorForm);
        toast.success('Vendor ditambahkan');
      }
      setOpenVendor(false);
      setEditingVendorId(null);
      setVendorForm({ name: '', contact: '', address: '', notes: '' });
      if (autoSelectAfterCreate && created) {
        setBillForm((f) => ({ ...f, vendor_id: created.id }));
        setAutoSelectAfterCreate(false);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
    }
  };

  const openVendorCreate = () => {
    setVendorForm({ name: '', contact: '', address: '', notes: '' });
    setEditingVendorId(null);
    setOpenVendor(true);
  };
  const openVendorEdit = (v) => {
    setVendorForm({
      name: v.name || '', contact: v.contact || '',
      address: v.address || '', notes: v.notes || '',
    });
    setEditingVendorId(v.id);
    setOpenVendor(true);
  };

  const openBillCreate = () => {
    setBillForm((f) => ({
      vendor_id: '', date: todayIso(), description: '', amount: '', term_days: 0,
      expense_account_id: accounts.find((x) => x.code === '1-1400')?.id || '',
      payable_account_id: accounts.find((x) => x.code === '2-1000')?.id || '',
      attachment_url: '', attachment_name: '',
    }));
    setEditingBillId(null);
    setOpenBill(true);
  };
  const openBillEdit = (b) => {
    setBillForm({
      vendor_id: b.vendor_id,
      date: b.date,
      description: b.description || '',
      amount: b.amount ? new Intl.NumberFormat('id-ID').format(b.amount) : '',
      term_days: Number(b.term_days || 0),
      expense_account_id: b.expense_account_id,
      payable_account_id: b.payable_account_id,
      attachment_url: b.attachment_url || '',
      attachment_name: b.attachment_name || '',
    });
    setEditingBillId(b.id);
    setOpenBill(true);
  };

  const removeVendor = async (v) => {
    if (!window.confirm(`Hapus vendor ${v.name}?`)) return;
    try { await deleteVendor(v.id); toast.success('Vendor dihapus'); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  const openPay = (b) => {
    const sisa = Math.max(0, (b.amount || 0) - (b.paid_amount || 0));
    const kas = accounts.find((x) => x.code === '1-1000') || accounts.find((x) => x.type === 'asset');
    setPayBill(b);
    setPayForm({
      date: todayIso(),
      amount: new Intl.NumberFormat('id-ID').format(sisa),
      cash_account_id: kas?.id || '',
      notes: '',
    });
  };

  const savePayment = async () => {
    if (!payBill) return;
    const amt = parseNumber(payForm.amount);
    if (amt <= 0) return toast.error('Nominal harus > 0');
    if (!payForm.cash_account_id) return toast.error('Pilih akun kas/bank');
    try {
      await createBillPayment(payBill.id, {
        date: payForm.date,
        amount: amt,
        cash_account_id: payForm.cash_account_id,
        notes: payForm.notes || '',
      });
      toast.success('Pembayaran tercatat & jurnal dibuat');
      setPayBill(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
    }
  };

  const saveBill = async () => {
    const amt = parseNumber(billForm.amount);
    if (!billForm.vendor_id) return toast.error('Pilih vendor');
    if (!billForm.expense_account_id) return toast.error('Pilih akun beban/aset');
    if (!billForm.payable_account_id) return toast.error('Pilih akun hutang');
    if (amt <= 0) return toast.error('Nominal harus > 0');
    const termDays = Math.max(0, Number(billForm.term_days || 0));
    try {
      if (editingBillId) {
        await updateVendorBill(editingBillId, { ...billForm, amount: amt, term_days: termDays });
        toast.success('Tagihan diperbarui & jurnal disinkron');
      } else {
        await createVendorBill({ ...billForm, amount: amt, term_days: termDays });
        toast.success('Tagihan tersimpan & terjurnal');
      }
      setOpenBill(false);
      setEditingBillId(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal simpan');
    }
  };

  const removeBill = async (b) => {
    if (!window.confirm(`Hapus tagihan ${b.vendor_name} Rp${b.amount.toLocaleString('id-ID')}? Jurnal terkait juga terhapus.`)) return;
    try { await deleteVendorBill(b.id); toast.success('Tagihan dihapus'); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Maks 10 MB'); e.target.value = ''; return; }
    setUploading(true);
    try {
      const r = await uploadFile(f);
      setBillForm((s) => ({ ...s, attachment_url: r.url, attachment_name: r.original_name }));
      toast.success('Bukti diunggah');
    } catch (err) { toast.error('Gagal unggah'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const editingBill = editingBillId ? enriched.find((x) => x.id === editingBillId) : null;
  const isCustomTempo = !TEMPO_PRESETS.includes(Number(billForm.term_days));

  return (
    <AccountingLayout>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
            JurnalKu · Hutang Usaha
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-800" /> Vendor & Tagihan
          </h1>
          <p className="text-sm text-stone-600 mt-2">
            Setiap tagihan yang disimpan otomatis membuat jurnal <span className="font-mono text-xs">D: 1-1400 Persediaan Barang</span> · <span className="font-mono text-xs">K: 2-1000 Hutang Usaha</span> (default — bisa diubah per tagihan).
          </p>
        </div>
        <div className="flex gap-2">
          {tab === 'vendors' ? (
            <Button onClick={openVendorCreate} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-1" /> Vendor Baru
            </Button>
          ) : (
            <Button onClick={openBillCreate} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="add-bill-btn">
              <Plus className="w-4 h-4 mr-1" /> Tagihan Baru
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <Button variant={tab === 'bills' ? 'default' : 'outline'} onClick={() => setTab('bills')}
          className={tab === 'bills' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
          data-testid="tab-bills"
        >
          <Receipt className="w-4 h-4 mr-1.5" /> Tagihan
        </Button>
        <Button variant={tab === 'vendors' ? 'default' : 'outline'} onClick={() => setTab('vendors')}
          className={tab === 'vendors' ? 'bg-emerald-800 hover:bg-emerald-900 text-white' : ''}
          data-testid="tab-vendors"
        >
          <Building2 className="w-4 h-4 mr-1.5" /> Daftar Vendor
        </Button>
      </div>

      {tab === 'bills' && (
        <>
          {/* Summary hutang keseluruhan */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-4" data-testid="ap-total">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Hutang</div>
              <div className="text-xl font-semibold text-stone-900 mt-1 font-mono">{formatRp(totalHutang)}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-4" data-testid="ap-not-due">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Belum Jatuh Tempo</div>
              <div className="text-xl font-semibold text-emerald-800 mt-1 font-mono">{formatRp(belumTempoAmt)}</div>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/40 px-5 py-4" data-testid="ap-overdue">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Jatuh Tempo</div>
              <div className="text-xl font-semibold text-red-700 mt-1 font-mono">{formatRp(terlambatAmt)}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-4" data-testid="ap-count">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Jumlah Tagihan</div>
              <div className="text-xl font-semibold text-stone-900 mt-1">{activeBills.length} <span className="text-sm font-normal text-stone-500">Tagihan</span></div>
            </div>
          </div>

          {/* Aging hutang berdasarkan sisa */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-3" data-testid="ap-aging-current">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Current · 0–30 hari</div>
              <div className="text-base font-semibold text-stone-900 mt-0.5 font-mono">{formatRp(aging.current)}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-3" data-testid="ap-aging-31-60">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">31–60 Hari</div>
              <div className="text-base font-semibold text-stone-900 mt-0.5 font-mono">{formatRp(aging.d3160)}</div>
            </div>
            <div className="rounded-xl border border-stone-200 bg-white px-5 py-3" data-testid="ap-aging-61-90">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">61–90 Hari</div>
              <div className="text-base font-semibold text-stone-900 mt-0.5 font-mono">{formatRp(aging.d6190)}</div>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50/40 px-5 py-3" data-testid="ap-aging-90">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">&gt;90 Hari</div>
              <div className="text-base font-semibold text-red-700 mt-0.5 font-mono">{formatRp(aging.d90)}</div>
            </div>
          </div>

          {/* Filter + sort + export */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <select value={fPay} onChange={(e) => setFPay(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
              data-testid="bill-filter-pay">
              <option value="all">Semua Status</option>
              <option value="belum">Belum Dibayar</option>
              <option value="sebagian">Sebagian Dibayar</option>
              <option value="lunas">Lunas</option>
            </select>
            <select value={fTempo} onChange={(e) => setFTempo(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
              data-testid="bill-filter-tempo">
              <option value="all">Semua Tempo</option>
              <option value="belum_tempo">Belum Jatuh Tempo</option>
              <option value="hari_ini">Jatuh Tempo Hari Ini</option>
              <option value="terlambat">Terlambat</option>
            </select>
            <select value={fAge} onChange={(e) => setFAge(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
              data-testid="bill-filter-age">
              <option value="all">Semua Umur</option>
              {AGE_BUCKETS.map((b) => <option key={b.k} value={b.k}>{b.label}</option>)}
            </select>
            <select value={fVendor} onChange={(e) => setFVendor(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white min-w-[150px]"
              data-testid="bill-filter-vendor">
              <option value="all">Semua Vendor</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
              className="h-9 text-xs w-[135px]" data-testid="bill-filter-from" />
            <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
              className="h-9 text-xs w-[135px]" data-testid="bill-filter-to" />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white"
              data-testid="bill-sort">
              {SORT_OPTS.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
            </select>
            {filterActive && (
              <button onClick={resetFilters}
                className="text-xs font-medium text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-100 transition inline-flex items-center gap-1"
                data-testid="bill-filter-reset">
                <X className="w-3 h-3" /> Reset
              </button>
            )}
            <div className="ml-auto">
              <Button onClick={() => exportBillsToExcel(selectedBillIds.size > 0 ? selectedBills : filtered)} variant="outline"
                data-testid="bills-export-btn">
                <Download className="w-4 h-4 mr-1.5" /> Export Excel
              </Button>
            </div>
          </div>

          <div className="card-modern overflow-x-auto">
            <table className="w-full ledger min-w-[1280px]">
              <thead>
                <tr>
                  <th style={{ width: '3%' }} className="text-center">
                    <input type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllBills}
                      className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer align-middle"
                      data-testid="bill-check-all"
                      aria-label="Pilih semua" />
                  </th>
                  <th style={{ width: '9%' }}>Tanggal</th>
                  <th>Vendor</th>
                  <th>Deskripsi</th>
                  <th style={{ width: '10%' }} className="text-right">Nominal</th>
                  <th style={{ width: '10%' }} className="text-right">Sisa</th>
                  <th style={{ width: '6%' }}>Tempo</th>
                  <th style={{ width: '13%' }}>Jatuh Tempo</th>
                  <th style={{ width: '7%' }}>Umur Hutang</th>
                  <th style={{ width: '8%' }}>Status</th>
                  <th style={{ width: '7%' }}>Ref</th>
                  <th style={{ width: '12%' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const checked = selectedBillIds.has(b.id);
                  const ts = TEMPO_STATUS[b.tempoStatus];
                  return (
                  <tr key={b.id} className={checked ? 'bg-emerald-50/40' : ''} data-testid={`bill-row-${b.id}`}>
                    <td className="text-center">
                      <input type="checkbox"
                        checked={checked}
                        onChange={() => toggleBill(b.id)}
                        className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer align-middle"
                        data-testid={`bill-check-${b.id}`}
                        aria-label="Pilih tagihan" />
                    </td>
                    <td className="num text-sm">{b.date}</td>
                    <td className="font-medium text-stone-800">{b.vendor_name}</td>
                    <td className="text-sm text-stone-600">
                      {b.description || '—'}
                      {b.attachment_url && (
                        <a href={attachmentUrl(b.attachment_url)} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 ml-2 text-emerald-800 text-xs">
                          <Paperclip className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="num-cell">{formatRp(b.amount)}</td>
                    <td className={`num-cell ${b.sisa > 0 ? 'text-red-700' : 'text-emerald-800'}`}>{formatRp(b.sisa)}</td>
                    <td className="text-xs text-stone-600">{tempoLabel(b)}</td>
                    <td>
                      <div className="num text-xs text-stone-700">{fmtDateID(b.due)}</div>
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded border mt-0.5 ${ts.cls}`}
                        data-testid={`bill-tempo-status-${b.id}`}>
                        {b.tempoStatus === 'belum_tempo' && `${b.daysToDue} hari lagi`}
                        {b.tempoStatus === 'hari_ini' && 'Jatuh tempo hari ini'}
                        {b.tempoStatus === 'terlambat' && `Terlambat ${-b.daysToDue} hari`}
                        {b.tempoStatus === 'lunas' && 'Lunas'}
                      </span>
                    </td>
                    <td className={`text-xs ${b.lunas ? 'text-emerald-800 font-semibold' : 'text-stone-700'}`}
                      data-testid={`bill-age-${b.id}`}>
                      {b.lunas ? 'LUNAS' : `${b.age} hari`}
                    </td>
                    <td>
                      {b.payStatus === 'lunas' ? (
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded">Lunas</span>
                      ) : b.payStatus === 'sebagian' ? (
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">Sebagian</span>
                      ) : (
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider bg-stone-100 text-stone-600 border border-stone-200 px-1.5 py-0.5 rounded">Belum</span>
                      )}
                    </td>
                    <td className="font-mono text-xs text-emerald-900">{b.journal_ref}</td>
                    <td>
                      <div className="flex gap-1">
                        {b.sisa > 0 && (
                          <Button variant="outline" size="sm" onClick={() => openPay(b)}
                            className="border-emerald-300 bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white"
                            data-testid={`pay-bill-${b.id}`} title="Bayar hutang">
                            <Wallet className="w-3 h-3 mr-1" /> Bayar
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openBillEdit(b)}
                          className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                          data-testid={`edit-bill-${b.id}`} title="Ubah tagihan">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => removeBill(b)}
                          className="border-red-200 text-red-700 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={12} className="text-center text-stone-500 py-8">
                    {bills.length === 0 ? 'Belum ada tagihan.' : 'Tidak ada tagihan yang cocok dengan filter.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary bar tagihan terpilih */}
          {selectedBillIds.size > 0 && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bill-selection-bar">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="font-semibold text-emerald-900" data-testid="bill-selected-count">
                  {selectedBills.length} Tagihan Dipilih
                </span>
                <span className="text-stone-700">
                  Total Hutang: <span className="font-mono font-semibold text-emerald-900" data-testid="bill-selected-total">{formatRp(selectedTotal)}</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => exportBillsToExcel(selectedBills)} size="sm"
                  className="bg-emerald-800 hover:bg-emerald-900 text-white h-8 text-xs"
                  data-testid="bill-export-selected">
                  <Download className="w-3.5 h-3.5 mr-1" /> Export {selectedBills.length} Tagihan
                </Button>
                <button onClick={() => setSelectedBillIds(new Set())}
                  className="text-xs font-medium text-emerald-800 hover:text-emerald-900 px-2 py-1 rounded hover:bg-emerald-100 transition inline-flex items-center gap-1"
                  data-testid="bill-selection-clear">
                  <X className="w-3 h-3" /> Batal Pilih
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'vendors' && (
        <div className="card-modern overflow-hidden">
          <table className="w-full ledger">
            <thead>
              <tr>
                <th>Nama</th>
                <th style={{ width: '20%' }}>Kontak</th>
                <th>Alamat</th>
                <th style={{ width: '10%' }}></th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`}>
                  <td className="font-medium text-stone-800">{v.name}</td>
                  <td className="text-sm text-stone-600">{v.contact || '—'}</td>
                  <td className="text-sm text-stone-600">{v.address || '—'}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openVendorEdit(v)}
                        className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                        data-testid={`edit-vendor-${v.id}`} title="Ubah vendor">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => removeVendor(v)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && (
                <tr><td colSpan={4} className="text-center text-stone-500 py-8">Belum ada vendor.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Vendor dialog */}
      <Dialog open={openVendor} onOpenChange={setOpenVendor}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVendorId ? 'Edit Vendor' : 'Vendor Baru'}</DialogTitle>
            <DialogDescription>Data supplier / vendor.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nama *</Label>
              <Input value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                data-testid="vendor-form-name" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Kontak (HP/Email)</Label>
              <Input value={vendorForm.contact} onChange={(e) => setVendorForm({ ...vendorForm, contact: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Alamat</Label>
              <Input value={vendorForm.address} onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Catatan</Label>
              <Textarea rows={2} value={vendorForm.notes} onChange={(e) => setVendorForm({ ...vendorForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenVendor(false)}>Batal</Button>
            <Button onClick={saveVendor} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="vendor-form-save">
              <Save className="w-4 h-4 mr-1" /> Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill dialog */}
      <Dialog open={openBill} onOpenChange={setOpenBill}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBillId ? 'Edit Tagihan Vendor' : 'Tagihan Vendor Baru'}</DialogTitle>
            <DialogDescription>Akan otomatis {editingBillId ? 'mengubah' : 'membuat'} jurnal Debit Persediaan/Beban · Kredit Hutang Usaha.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600">Vendor *</Label>
                  <button
                    type="button"
                    onClick={() => { setAutoSelectAfterCreate(true); setOpenVendor(true); }}
                    className="text-[11px] font-semibold text-emerald-800 hover:underline flex items-center gap-1"
                    data-testid="bill-form-add-vendor"
                  >
                    <Plus className="w-3 h-3" /> Tambah Vendor Baru
                  </button>
                </div>
                <Select value={billForm.vendor_id} onValueChange={(v) => setBillForm({ ...billForm, vendor_id: v })}>
                  <SelectTrigger className="bg-white" data-testid="bill-form-vendor">
                    <SelectValue placeholder="Pilih vendor…" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Tagihan *</Label>
                <Input type="date" value={billForm.date} onChange={(e) => setBillForm({ ...billForm, date: e.target.value })}
                  data-testid="bill-form-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tempo Pembayaran</Label>
                <Select
                  value={isCustomTempo ? 'custom' : String(Number(billForm.term_days || 0))}
                  onValueChange={(v) => {
                    if (v === 'custom') {
                      setBillForm({ ...billForm, term_days: isCustomTempo ? Number(billForm.term_days) : 21 });
                    } else {
                      setBillForm({ ...billForm, term_days: Number(v) });
                    }
                  }}>
                  <SelectTrigger className="bg-white" data-testid="bill-form-tempo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Cash / 0 hari</SelectItem>
                    {TEMPO_PRESETS.slice(1).map((d) => (
                      <SelectItem key={d} value={String(d)}>{d} hari</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {isCustomTempo && (
                  <Input type="number" min={0} value={billForm.term_days}
                    onChange={(e) => setBillForm({ ...billForm, term_days: Math.max(0, parseInt(e.target.value || '0', 10) || 0) })}
                    className="mt-2 text-right font-mono" placeholder="Jumlah hari"
                    data-testid="bill-form-tempo-custom" />
                )}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Jatuh Tempo (otomatis)</Label>
                <div className="h-10 flex items-center px-3 rounded-md border border-stone-200 bg-stone-50 text-sm font-mono text-stone-700"
                  data-testid="bill-form-due-preview">
                  {billForm.date ? fmtDateID(addDays(billForm.date, Number(billForm.term_days || 0))) : '—'}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Deskripsi</Label>
              <Input value={billForm.description} onChange={(e) => setBillForm({ ...billForm, description: e.target.value })}
                placeholder="mis. Pembelian shuttlecock 5 slop" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nominal (Rp) *</Label>
              <Input value={billForm.amount}
                onChange={(e) => setBillForm({ ...billForm, amount: formatThousands(e.target.value) })}
                className="text-right font-mono" placeholder="0"
                data-testid="bill-form-amount" />
            </div>
            {editingBill && (
              <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="bill-edit-info">
                <div className="flex justify-between">
                  <span className="text-stone-600">Sudah Dibayar</span>
                  <span className="num text-emerald-800">{formatRp(editingBill.paid)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Sisa Hutang</span>
                  <span className="num font-semibold text-red-700">{formatRp(editingBill.sisa)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Jatuh Tempo</span>
                  <span className="font-mono">{fmtDateID(editingBill.due)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-600">Status</span>
                  <span className="font-semibold">
                    {editingBill.payStatus === 'lunas' ? 'LUNAS' : editingBill.payStatus === 'sebagian' ? 'SEBAGIAN DIBAYAR' : 'BELUM DIBAYAR'}
                    {' · '}{TEMPO_STATUS[editingBill.tempoStatus].label}
                  </span>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Akun Debit (Beban/Aset) *</Label>
                <AccountCombobox
                  value={billForm.expense_account_id}
                  onChange={(v) => setBillForm({ ...billForm, expense_account_id: v })}
                  accounts={expenseAccounts}
                  testId="bill-form-expense"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Akun Hutang (Kredit) *</Label>
                <AccountCombobox
                  value={billForm.payable_account_id}
                  onChange={(v) => setBillForm({ ...billForm, payable_account_id: v })}
                  accounts={liabilityAccounts}
                  testId="bill-form-payable"
                />
              </div>
            </div>
            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/50 flex items-center gap-3">
              <div className="flex items-center gap-2 text-stone-700">
                <Paperclip className="w-4 h-4 text-emerald-800" />
                <span className="text-[11px] uppercase tracking-widest text-stone-600 font-semibold">Invoice / Faktur</span>
              </div>
              {billForm.attachment_url ? (
                <div className="flex items-center gap-2 flex-1">
                  <a href={attachmentUrl(billForm.attachment_url)} target="_blank" rel="noreferrer"
                    className="text-sm text-emerald-800 hover:underline truncate max-w-[300px]">
                    {billForm.attachment_name}
                  </a>
                  <Button type="button" variant="outline" size="sm" onClick={() => setBillForm({ ...billForm, attachment_url: '', attachment_name: '' })}
                    className="border-red-200 text-red-700 h-8">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={onFile} className="hidden" disabled={uploading} />
                  <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border ${uploading ? 'bg-stone-100 text-stone-400' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>
                    <Upload className="w-3.5 h-3.5" /> {uploading ? 'Mengunggah…' : 'Pilih File'}
                  </span>
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenBill(false)}>Batal</Button>
            <Button onClick={saveBill} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="bill-form-save">
              <Save className="w-4 h-4 mr-1" /> Simpan &amp; Jurnal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay bill dialog */}
      <Dialog open={!!payBill} onOpenChange={(v) => { if (!v) setPayBill(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bayar Hutang Vendor</DialogTitle>
            <DialogDescription>
              Otomatis membuat jurnal <span className="font-mono text-xs">D: Hutang Usaha · K: Kas/Bank</span>.
            </DialogDescription>
          </DialogHeader>
          {payBill && (
            <div className="space-y-3">
              <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-stone-600">Vendor</span>
                  <span className="font-semibold">{payBill.vendor_name}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-stone-600">Total Tagihan</span>
                  <span className="num font-medium">{formatRp(payBill.amount)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span className="text-stone-600">Sudah Dibayar</span>
                  <span className="num text-emerald-800">{formatRp(payBill.paid_amount || 0)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-stone-300">
                  <span className="text-stone-700 font-semibold">Sisa</span>
                  <span className="num font-bold text-red-700">
                    {formatRp(Math.max(0, (payBill.amount || 0) - (payBill.paid_amount || 0)))}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tanggal Bayar *</Label>
                  <Input type="date" value={payForm.date}
                    onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                    data-testid="pay-form-date" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Nominal (Rp) *</Label>
                  <Input value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: formatThousands(e.target.value) })}
                    className="text-right font-mono" placeholder="0"
                    data-testid="pay-form-amount" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Dibayar dari (Kas/Bank) *</Label>
                <AccountCombobox
                  value={payForm.cash_account_id}
                  onChange={(v) => setPayForm({ ...payForm, cash_account_id: v })}
                  accounts={accounts.filter((a) => a.type === 'asset')}
                  testId="pay-form-cash"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Catatan</Label>
                <Input value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  placeholder="opsional" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayBill(null)}>Batal</Button>
            <Button onClick={savePayment}
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid="pay-form-save">
              <Wallet className="w-4 h-4 mr-1" /> Bayar &amp; Jurnal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}
